import * as Sentry from "@sentry/nextjs"
import { useTagManager } from "../analytics/useTagManager"
import { toApiError } from "@/utils/errors/toApiError"

type TagValue = string | number | boolean | null | undefined
export type Tags = Record<string, TagValue>
export type Extras = Record<string, unknown>
export type Context = Record<string, unknown>

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'Unknown error'
}

/**
 * Sentry serializes a non-Error object to "[object Object]" / "Object captured
 * as exception with keys: …". Wrap plain API error objects into a real Error
 * (preserving their fields) so the captured event has a usable message + stack.
 */
const toCaptureable = (error: unknown): unknown => {
  if (error instanceof Error) return error
  if (error && typeof error === 'object') {
    return toApiError(error as Record<string, unknown>)
  }
  return error
}

export const useSentry = () => {
  const { sendGtm } = useTagManager()
  const setTag = (key: string, value: TagValue) => {
    if (value === undefined || value === null) return
    Sentry.setTag(key, String(value))
  }

  const setTags = (tags: Tags) => {
    Object.entries(tags).forEach(([key, value]) => setTag(key, value))
  }

  const setUser = (user: Sentry.User | null | undefined) => {
    Sentry.setUser(user ?? null)
  }

  const setExtra = (key: string, value: unknown) => {
    Sentry.setExtra(key, value)
  }

  const setContext = (name: string, context: Context | null) => {
    Sentry.setContext(name, context)
  }

  /**
   * `asBreadcrumb: true` records the failure as a Sentry breadcrumb instead of
   * an exception event. For outcomes that are expected and not actionable (e.g.
   * a card issuer refusing a 3DS challenge) one exception per occurrence is pure
   * noise, but the failure is still worth keeping as session context on a later
   * real error. GTM analytics fire either way, so decline-rate reporting is
   * unaffected. See FZ-WEB-NEXTJS-ER.
   */
  const captureError = (
    error: unknown,
    extras: Extras = {},
    level: Sentry.SeverityLevel = 'error',
    { asBreadcrumb = false }: { asBreadcrumb?: boolean } = {},
  ) => {
    const captureable = toCaptureable(error)
    const errorMessage = getErrorMessage(captureable)

    if (asBreadcrumb) {
      Sentry.addBreadcrumb({
        category: 'handled-error',
        type: 'error',
        level,
        message: errorMessage,
        data: extras,
      })
      sendGtm.UI_ERROR_CAPTURED({ error: errorMessage })
      return
    }

    Sentry.withScope((scope) => {
      scope.setLevel(level)
      scope.setTag('manual_capture', 'true')
      scope.setExtra('manual_capture', true)
      if (Object.keys(extras).length > 0) {
        scope.setExtras(extras)
      }

      Sentry.captureException(captureable)
      sendGtm.UI_ERROR_CAPTURED({ error: errorMessage })
    })
  }

  return {
    setTag,
    setTags,
    setUser,
    setExtra,
    setContext,
    captureError,
  }
}
